

import { mapGetters } from 'vuex';
import { removeWhitespaces } from '@sopheos/orbjs_rules';
// API
import * as apiComment from '../api/private/comment';
import errorsMixin from '../mixins/error-handler';

export default {
  name: 'SDetailCommentForm',
  mixins: [errorsMixin],
  props: {
    playgroundId: {
      type: Number,
      required: true,
    },
    modeEdit: {
      type: Boolean,
      required: true,
    },
    commentId: {
      default: null,
      type: Number,
    },
    text: {
      default: null,
      type: String,
    },
    grade: {
      default: null,
      type: Number,
    },
  },
  data() {
    return {
      fieldsRefs: ['comment-text', 'comment-form', 'comment-grade', 'comment-playground', 'http-404'],
      commentGrade: null,
      commentText: null,
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      isAuth: 'authToken/isAuth',
      isLarge: 'screen/isLarge',
    }),
    commentTextIsSet() {
      // The comment is not null and is not empty
      return this.commentText?.length > 0;
    },
    commentTextIsValid() {
      // The comment contains maximum 500 characters
      return this.commentTextIsSet && this.commentText.length <= 500;
    },
    displayCommentField() {
      return this.commentGrade > 0 && this.isAuth;
    },
  },
  watch: {
    isAuth() {
      if (this.displayCommentField) {
        this.$nextTick(() => {
          this.$refs['comment-text'].focus();
          this.$nextTick(() => {
            this.$refs.commentForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          });
        });
      } else {
        this.closeCommentForm();
      }
    },
    playgroundId() {
      this.closeCommentForm();
    },
    commentGrade() {
      if (this.commentGrade > 0 && this.showLogin()) {
        this.$nextTick(() => {
          this.$refs['comment-text'].focus();
          this.$nextTick(() => {
            this.$refs.commentForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          });
        });
      } else {
        this.errors$.reset();
        this.commentText = null;
      }
    },
    commentText() {
      // When the comment changes, if it is not set or if it is valid, set the error to false
      if (!this.commentTextIsSet || this.commentTextIsValid) {
        this.errors$.handleError(false, 'comment', 'text');
      }
    },
    displayCommentField() {
      if (this.displayCommentField) {
        this.$emit('has-unsave', true);
      }
    },
  },
  mounted() {
    this.commentGrade = this.grade;
    this.commentText = this.text;

    this.$nextTick(() => {
      if (this.$refs['comment-text']) {
        this.$refs['comment-text'].focus();
      }
    });
  },
  beforeDestroy() {
    if (this.displayCommentField) {
      this.$emit('has-unsave', false);
    }
  },
  methods: {
    closeCommentForm() {
      if (this.displayCommentField) {
        this.$emit('has-unsave', false);
      }
      this.commentGrade = null;
    },
    showLogin() {
      if (!this.isAuth) {
        this.$emit('login');
      }
      return this.isAuth;
    },
    cancel() {
      if (this.modeEdit) {
        this.$emit('close-comment');
      } else {
        this.closeCommentForm();
      }
    },
    validate() {
      if (this.modeEdit) {
        this.editComment();
      } else {
        this.postComment();
      }
    },
    postComment() {
      if (this.showLogin()) {
        this.commentText = removeWhitespaces(this.commentText);
        // prevalidation
        this.errors$.reset();
        this.errors$.handleError(!this.commentGrade, 'comment', 'grade');
        this.errors$.handleError(this.commentTextIsSet && !this.commentTextIsValid, 'comment', 'text');
        if (this.errors$.hasErrors()) {
          this.errors$.focusFirstError();
          return;
        }

        this.loading = true;
        this.$emit('status-comment-form-api-call', 'LOADING');
        apiComment.addComment({ playground: this.playgroundId, text: this.commentText, grade: this.commentGrade }, this)
          .then((res) => {
            this.$emit('add-comment', { ...res, text: this.commentText, grade: this.commentGrade });
            this.closeCommentForm();
            this.$emit('status-comment-form-api-call', 'SUCCESS');
          })
          .catch((error) => {
            this.$emit('status-comment-form-api-call', 'ERROR');
            // Validation error
            if (error.response && error.response.status === 400) {
              if (this.errors$.hasErrors()) {
                this.errors$.focusFirstError();
                return;
              }
              this.errors$.handleError(true, 'comment', 'form');
              this.$nextTick(() => {
                this.$refs['comment-form'].$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              });
            }
          }).finally(() => {
            this.loading = false;
          });
      }
    },
    editComment() {
      this.commentText = removeWhitespaces(this.commentText);
      // prevalidation
      this.errors$.reset();
      this.errors$.handleError(!this.commentGrade, 'comment', 'grade');
      if (this.errors$.hasErrors()) {
        this.errors$.focusFirstError();
        return;
      }

      this.loading = true;
      this.$emit('status-comment-form-api-call', 'LOADING');
      const comment = { text: this.commentText, grade: this.commentGrade };
      apiComment.editComment(this.commentId, comment, this).then((res) => {
        this.$emit('edit-comment', { ...res, ...comment, id: this.commentId });
        this.$emit('close-comment');
        this.closeCommentForm();
        this.$emit('status-comment-form-api-call', 'SUCCESS');
      }).catch((error) => {
        this.$emit('status-comment-form-api-call', 'ERROR');
        // Validation error
        if (error.response && (error.response.status === 400 || error.response.status === 404)) {
          if (this.errors$.hasErrors()) {
            this.errors$.focusFirstError();
            return;
          }
          this.errors$.handleError(true, 'comment', 'form');
          this.$nextTick(() => {
            this.$refs['comment-form'].$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
      }).finally(() => {
        this.loading = false;
      });
    },
  },
};
